export const FormatMixin = {
  methods: {
    // Text formatting methods
    capitalize(value) {
      if (!value) return '';
      return value
        .split(' ')
        .map(val => val.charAt(0).toUpperCase() + val.substr(1).toLowerCase())
        .join(' ');
    },

    truncate(text, length = 100, clamp = '...') {
      if (!text) return '';
      const node = document.createElement('div');
      node.innerHTML = text;
      const content = node.textContent;
      return content.length > length ? content.slice(0, length) + clamp : content;
    },

    truncateText(text, length = 100) {
      return this.truncate(text, length);
    },

    // Money/Currency formatting methods
    formatMoney(value) {
      if (value == null) return '$0.00';
      const amount = value / 100;
      return `$${amount.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')}`;
    },

    formatCurrency(amount, currency = 'USD') {
      if (amount == null) return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(0);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    },
    
    // Number formatting methods
    formatNumber(number, options = {}) {
      if (number == null) return '0';
      return new Intl.NumberFormat('en-US', options).format(number);
    },

    // Date formatting methods (Vue 3 compatible - no filters)
    formatDate(value, format = 'MMMM D, YYYY') {
      if (!value) return '';
      // If dayjs is available, use it, otherwise fallback to Intl
      if (typeof dayjs !== 'undefined') {
        return dayjs(value).format(format);
      }
      
      const defaultOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(new Date(value));
    },

    formatShortDate(value) {
      if (!value) return '';
      if (typeof dayjs !== 'undefined') {
        return dayjs(value).format('MM/DD/YYYY');
      }
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(value));
    },

    formatShortDateTime(value) {
      if (!value) return '';
      if (typeof dayjs !== 'undefined') {
        return dayjs(value).format('MM/DD/YYYY hh:mma');
      }
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(new Date(value));
    },

    formatDateAgo(value) {
      if (!value) return '';
      if (typeof dayjs !== 'undefined') {
        return dayjs(value).from(dayjs());
      }
      
      // Fallback relative time formatting
      const now = new Date();
      const date = new Date(value);
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    },

    // Enhanced date formatting with options
    formatDateWithOptions(date, options = {}) {
      if (!date) return '';
      const defaultOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(new Date(date));
    },
  },
};

export const DotnetMixin = {
  methods: {
    // Modern .NET error processing from D2Kit
    processOnRenderErrors(errors) {
      const mappedErrors = {};
      errors.forEach((error) => {
        if (error.value?.errors?.[0]?.errorMessage) {
          const key = error.key ? error.key.toLowerCase() : 'generic';
          const errorMessage = error.value.errors[0].errorMessage;
          mappedErrors[key] = Array.isArray(errorMessage) ? errorMessage : [errorMessage];
        }
      });
      
      // Set errors on form observer if available
      if (this.$refs?.formObserver?.setErrors) {
        this.$refs.formObserver.setErrors(mappedErrors);
      }
      
      return mappedErrors;
    },
    
    // Legacy error formatting method for backward compatibility
    formatModelError(error) {
      if (error.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(', ');
      }
      return error.message || 'An error occurred';
    },
    
    // Enhanced date parsing with better error handling
    parseDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    },
    
    // Additional .NET helper methods
    formatValidationErrors(validationResult) {
      if (!validationResult?.errors) return {};
      
      const formattedErrors = {};
      Object.entries(validationResult.errors).forEach(([field, messages]) => {
        formattedErrors[field.toLowerCase()] = Array.isArray(messages) ? messages : [messages];
      });
      
      return formattedErrors;
    },
    
    // Handle .NET model state errors
    handleModelStateErrors(response) {
      if (response?.data?.errors) {
        return this.formatValidationErrors(response.data);
      }
      return {};
    },
  },
};
